import styled from "@emotion/styled"
import { PLAYLIST_MODAL_Z_INDEX } from "theme/consts"
import { Popover } from "radix-ui"
import {
  MODAL_QUERY_ARCHIVE_KEY,
  MODAL_QUERY_BULLETIN_BOARD_TYPE_VALUE,
  MODAL_QUERY_KIDS_TYPE_VALUE,
  MODAL_QUERY_MANAGE_SCHOOLLIST_TYPE_VALUE,
} from "app/playlists/consts"
import DynamicSupportContent from "app/support/components/DynamicSupportContent"
import StaticSupportContent from "app/support/components/StaticSupportContent"
import useSupportContent from "app/support/hooks/useSupportContent"
import { SupportContentType } from "db/enums"
import { parseAsString, useQueryState } from "nuqs"
import {
  Dispatch,
  SetStateAction,
  Suspense,
  useCallback,
  useEffect,
  useState,
} from "react"
import { MODAL_BOX_SHADOW } from "theme/styles"
import Stack from "ui/Stack"
import { usePosthogWithCurrentUser } from "../../core/hooks/usePosthogWithCurrentUser"
import { GetCurrentUserResult } from "app/users/queries/getCurrentUser"
import CloseButtonBase from "ui/CloseButton"
import { AnimatePresence, motion } from "framer-motion"
import useAnimationDirectionVariants from "app/core/hooks/useAnimationDirectionVariants"

const AnimatedScreen = styled(motion.div)`
  /* position: absolute; */

  bottom: 0;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
`

const CloseButton = styled(CloseButtonBase)`
  position: fixed;
  bottom: 20px;
  right: 20px;
`

const OpenIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="56"
    height="56"
    viewBox="0 0 56 56"
  >
    <g transform="translate(-123 -82)">
      <rect
        width="56"
        height="56"
        rx="10"
        transform="translate(123 82)"
        className="background open-background"
      />
      <path
        d="M569.236,173.621c-4.985-7.75.209-16.516.209-16.516s4.407-9.619,18.673-8.015c11.33,1.275,21.881,6.269,19.647,21.174-1.7,11.338-16.007,13.268-19.766,12.489a.525.525,0,0,0-.508.168,11.445,11.445,0,0,1-6.205,3.434.519.519,0,0,1-.533-.822c1.372-1.47,2.734-4.182-2.317-5.676a15.367,15.367,0,0,1-9.2-6.237"
        transform="translate(-436.57 -57.139)"
        className="open-bubble"
      />
      <path
        d="M-6.507-14.175H-2.16A1.888,1.888,0,0,1-.108-16.227a1.822,1.822,0,0,1,1.944,1.971c.027,1.971-1.728,2.43-4.266,2.43H-4.05L-3.915-6.8H.135L.27-8.829c3.537-.027,6.048-1.917,6.048-5.4,0-3.753-2.619-5.778-6.372-5.778C-4.32-20.007-6.588-17.415-6.507-14.175ZM.891-2.268A2.551,2.551,0,0,0-1.836-4.806,2.559,2.559,0,0,0-4.59-2.268,2.55,2.55,0,0,0-1.836.216,2.542,2.542,0,0,0,.891-2.268Z"
        transform="translate(150.172 118.407) rotate(8)"
        className="open-question"
      />
    </g>
  </svg>
)

const PopoverContent = styled(Popover.Content)`
  z-index: ${PLAYLIST_MODAL_Z_INDEX + 1};
  pointer-events: all;
` //.withComponent(motion.div)

const PopoverContentContainer = styled(Stack)<{ fullHeight: boolean }>`
  ${MODAL_BOX_SHADOW};
  height: ${({ fullHeight }) => (fullHeight ? "510px" : "80px")};
  width: 340px;
  border-radius: 30px;
  max-height: 510px;
  z-index: ${PLAYLIST_MODAL_Z_INDEX + 1};
  pointer-events: all;
  display: flex;
  flex-direction: row;
  position: absolute;
  bottom: 0;
  right: 0;
  will-change: transform;
`

const PopoverTrigger = styled(Popover.Trigger)`
  z-index: ${PLAYLIST_MODAL_Z_INDEX + 1};
  pointer-events: all;
`

const getDefinitiveSupportType = ({
  supportType,
  library,
  archive,
}: {
  supportType?: SupportContentType
  library: string | null
  archive: string | null
}) => {
  if (library !== null) {
    if (library === MODAL_QUERY_KIDS_TYPE_VALUE) {
      return SupportContentType.LIBRARY_KIDS_LIST
    }
    if (library === MODAL_QUERY_BULLETIN_BOARD_TYPE_VALUE) {
      return SupportContentType.LIBRARY_BULLETIN_BOARD
    }
    if (library === MODAL_QUERY_MANAGE_SCHOOLLIST_TYPE_VALUE) {
      return SupportContentType.LIBRARY_MANAGE_SCHOOLLIST
    }
    if (library === "gedeeld") {
      return SupportContentType.LIBRARY_SHARE_LIST
    }
    return SupportContentType.LIBRARY_DEFAULT
  }

  if (archive !== null) {
    if (archive === MODAL_QUERY_KIDS_TYPE_VALUE) {
      return SupportContentType.ARCHIVE_KIDS_LIST
    }
    return SupportContentType.ARCHIVE_DEFAULT
  }

  return supportType || null
}

export type SupportTabs = "supportContent" | "staticSupportLinks"

const SupportPopover = ({
  supportContentType,
  isSupportButtonOpen,
  setIsSupportButtonOpen,
  currentUser,
}: {
  supportContentType?: SupportContentType
  isSupportButtonOpen: boolean
  setIsSupportButtonOpen: Dispatch<SetStateAction<boolean>>
  currentUser: GetCurrentUserResult
}) => {
  const [library] = useQueryState("bibliotheek", parseAsString)
  const [archive] = useQueryState(MODAL_QUERY_ARCHIVE_KEY, parseAsString)

  const defSupportContentType = getDefinitiveSupportType({
    supportType: supportContentType,
    library,
    archive,
  })

  const { supportContent, isLoading } = useSupportContent({
    supportContentType: defSupportContentType,
  })

  const [currentTab, setCurrentTab] = useState<SupportTabs>("supportContent")
  const analytics = usePosthogWithCurrentUser(currentUser)

  const closeButtonOnClick = useCallback(() => {
    if (!isSupportButtonOpen) {
      analytics.capture({
        name: "support_popover_opened",
        properties: {
          support_type: defSupportContentType,
        },
      })
    } else {
      analytics.capture({
        name: "support_popover_closed",
        properties: {
          support_type: defSupportContentType,
        },
      })
    }
    setIsSupportButtonOpen(!isSupportButtonOpen)
  }, [
    setIsSupportButtonOpen,
    isSupportButtonOpen,
    defSupportContentType,
    analytics,
  ])

  useEffect(() => {
    if (isSupportButtonOpen) {
      analytics.capture({
        name: "support_content_changed",
        properties: {
          support_type: defSupportContentType,
        },
      })
    }
  }, [isSupportButtonOpen, defSupportContentType, analytics])

  const variants = useAnimationDirectionVariants(
    currentTab === "staticSupportLinks" ? "forward" : "back",
    {
      enter: "340px",
      exit: "-340px",
      opacity: true,
    }
  )

  return (
    <Suspense>
      <Popover.Root
        open={isSupportButtonOpen}
        onOpenChange={setIsSupportButtonOpen}
      >
        <PopoverTrigger asChild>
          <CloseButton
            isOpen={isSupportButtonOpen}
            onClick={closeButtonOnClick}
            style={{
              viewTransitionName: "support-button",
            }}
          >
            {!isSupportButtonOpen && <OpenIcon />}
          </CloseButton>
        </PopoverTrigger>
        <>
          <AnimatePresence>
            {isSupportButtonOpen && (
              <Popover.Portal forceMount>
                <PopoverContent
                  forceMount
                  side={"top"}
                  sideOffset={8}
                  align="end"
                  onInteractOutside={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                  asChild
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0 }}
                    transition={{
                      type: "spring",
                      bounce: 0.2,
                      duration: 0.2,
                    }}
                    style={{
                      transformOrigin: "bottom right",
                      display: "grid",
                      // placeItems: "center",
                    }}
                  >
                    <AnimatePresence initial={false}>
                      <AnimatedScreen
                        key={currentTab}
                        variants={variants}
                        transition={{
                          type: "spring",
                          bounce: 0.2,
                          duration: 0.5,
                        }}
                        // eslint-disable-next-line @stringtale/no-string-jsx-attributes
                        initial="enter"
                        // eslint-disable-next-line @stringtale/no-string-jsx-attributes
                        animate="center"
                        // eslint-disable-next-line @stringtale/no-string-jsx-attributes
                        exit="exit"
                      >
                        <PopoverContentContainer
                          fullHeight={
                            !!(
                              currentTab !== "supportContent" || supportContent
                            )
                          }
                        >
                          {currentTab === "supportContent" && (
                            <DynamicSupportContent
                              supportContent={supportContent}
                              setCurrentTab={setCurrentTab}
                              currentUser={currentUser}
                              setIsSupportButtonOpen={setIsSupportButtonOpen}
                              key={defSupportContentType}
                            />
                          )}

                          {currentTab === "staticSupportLinks" && (
                            <StaticSupportContent
                              setCurrentTab={setCurrentTab}
                            />
                          )}
                        </PopoverContentContainer>
                      </AnimatedScreen>
                    </AnimatePresence>
                  </motion.div>
                </PopoverContent>
              </Popover.Portal>
            )}
          </AnimatePresence>
        </>
      </Popover.Root>
    </Suspense>
  )
}

export default SupportPopover
